import { FormEvent } from 'react';

interface Step6Props {
  data: Record<string, string>;
  onChange: (field: string, value: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export default function Step6Final({ data, onChange, onBack, onSubmit, isSubmitting }: Step6Props) {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="w-full max-w-xl">
      <p className="text-[#b6862d] text-xs tracking-widest uppercase font-sans mb-5">Step 6 of 6</p>
      <h2 className="font-serif text-4xl md:text-5xl text-[#141413] mb-3 leading-tight">A few final things.</h2>
      <p className="text-[#77776f] text-sm font-sans leading-relaxed mb-10">
        All optional — but every referral and extra detail helps us move faster.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-7">

        {/* Other villa referrals */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans flex items-center gap-2">
            Other Villa Owner Referrals
            <span className="text-[#77776f] text-[10px] normal-case tracking-normal font-sans">optional</span>
          </label>
          <textarea
            name="referrals"
            value={data.referrals || ''}
            onChange={(e) => onChange('referrals', e.target.value)}
            maxLength={500}
            rows={3}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors resize-none"
            placeholder="Know any other villa owners with padel courts? Names, contacts, locations — we'd love an introduction..."
          />
          <p className="text-[#77776f] text-xs font-sans text-right">{(data.referrals || '').length}/500</p>
        </div>

        {/* Local coach referrals */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans flex items-center gap-2">
            Local Coach Referrals
            <span className="text-[#77776f] text-[10px] normal-case tracking-normal font-sans">optional</span>
          </label>
          <textarea
            name="coachReferrals"
            value={data.coachReferrals || ''}
            onChange={(e) => onChange('coachReferrals', e.target.value)}
            maxLength={500}
            rows={3}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors resize-none"
            placeholder="Any local padel coaches you'd recommend to guests? Name, contact, level they coach..."
          />
          <p className="text-[#77776f] text-xs font-sans text-right">{(data.coachReferrals || '').length}/500</p>
        </div>

        {/* Anything else */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans flex items-center gap-2">
            Anything Else?
            <span className="text-[#77776f] text-[10px] normal-case tracking-normal font-sans">optional</span>
          </label>
          <textarea
            name="anythingElse"
            value={data.anythingElse || ''}
            onChange={(e) => onChange('anythingElse', e.target.value)}
            maxLength={500}
            rows={4}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors resize-none"
            placeholder="Questions, concerns, anything about your property or situation we should know before building your listing..."
          />
          <p className="text-[#77776f] text-xs font-sans text-right">{(data.anythingElse || '').length}/500</p>
        </div>

        <div className="flex items-center gap-4 mt-2">
          <button
            type="button"
            onClick={onBack}
            className="text-sm font-sans text-[#77776f] hover:text-[#3a3a34] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-arrow-left-line" /> Back
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-[#234012] text-[#fcfbf7] text-sm font-sans font-medium px-8 py-4 rounded-lg hover:bg-[#305a18] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-[#fcfbf7]/30 border-t-[#fcfbf7] rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit listing details'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
